import {
  L1ReviewStatus,
  L1TriggerReason,
  type L1ReviewItem,
} from "@/core/compliance/entities/L1Review";

// ---------------------------------------------------------------------------
// API DTO shapes (private to the details/ layer)
// ---------------------------------------------------------------------------

export interface L1ReviewItemDto {
  reviewId: string;
  scoringEventId: string;
  businessKey: string;
  firstName?: string | null;
  customerType?: string | null;
  lastName?: string | null;
  riskLevel?: string | null;
  status: string;
  triggerReason: string;
  matchesToQualify?: number | null;
  matchesConfirmed?: number | null;
  trackAScore?: number | null;
  trackBScore?: number | null;
  slaDeadline: string;
  slaExpired: boolean;
  assignedTo?: string | null;
  assignedAt?: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Mapping functions
// ---------------------------------------------------------------------------

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function mapStatus(raw: string): L1ReviewStatus {
  switch (raw) {
    case "PENDING": return L1ReviewStatus.PENDING;
    case "AUTHORIZED": return L1ReviewStatus.AUTHORIZED;
    case "SUSPENDED": return L1ReviewStatus.SUSPENDED;
    case "REJECTED": return L1ReviewStatus.REJECTED;
    case "ESCALATED": return L1ReviewStatus.ESCALATED;
    case "SLA_BREACHED": return L1ReviewStatus.SLA_BREACHED;
    default: return L1ReviewStatus.PENDING;
  }
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function mapTriggerReason(raw: string): L1TriggerReason {
  switch (raw) {
    case "PEP_HIT": return L1TriggerReason.PEP_HIT;
    case "SANCTIONS_HIT": return L1TriggerReason.SANCTIONS_HIT;
    case "ADVERSE_MEDIA": return L1TriggerReason.ADVERSE_MEDIA;
    case "FATF_GRAY_LIST": return L1TriggerReason.FATF_GRAY_LIST;
    case "HIGH_RISK": return L1TriggerReason.HIGH_RISK;
    case "MANUAL": return L1TriggerReason.MANUAL;
    case "VNEURONE_WEBHOOK_TIMEOUT": return L1TriggerReason.VNEURONE_WEBHOOK_TIMEOUT;
    case "EXTERNAL_ELIGIBILITY_REFUSED": return L1TriggerReason.EXTERNAL_ELIGIBILITY_REFUSED;
    case "SANCTIONED_COUNTRY": return L1TriggerReason.SANCTIONED_COUNTRY;
    // Un motif inconnu ne doit PAS devenir « Manuel » : l'agent lirait qu'un collègue a saisi la
    // revue à la main alors qu'elle vient d'un déclenchement automatique, et chercherait la
    // mauvaise chose. Mieux vaut avouer que le motif n'est pas reconnu.
    default: return L1TriggerReason.UNKNOWN;
  }
}

export function mapL1ReviewDtoToEntity(dto: L1ReviewItemDto): L1ReviewItem {
  return {
    reviewId: dto.reviewId,
    scoringEventId: dto.scoringEventId,
    businessKey: dto.businessKey,
    firstName: dto.firstName ?? null,
    customerType: dto.customerType ?? null,
    lastName: dto.lastName ?? null,
    riskLevel: dto.riskLevel ?? null,
    status: mapStatus(dto.status),
    triggerReason: mapTriggerReason(dto.triggerReason),
    matchesToQualify: dto.matchesToQualify ?? null,
    matchesConfirmed: dto.matchesConfirmed ?? null,
    trackAScore: dto.trackAScore ?? null,
    trackBScore: dto.trackBScore ?? null,
    slaDeadline: dto.slaDeadline,
    slaExpired: dto.slaExpired,
    assignedTo: dto.assignedTo ?? null,
    assignedAt: dto.assignedAt ?? null,
    createdAt: dto.createdAt,
  };
}
